import {
  createContext,
  forwardRef,
  type HTMLAttributes,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

import { autocompleteClasses, ListSubheader, styled } from '@mui/material'
import { type ListChildComponentProps, VariableSizeList } from 'react-window'

import AutocompleteFieldMultiControls from './AutocompleteFieldMultiControls'

const LISTBOX_PADDING = 8
// Starting estimates per row type. Real heights are measured at runtime (rows
// can differ: group headers are taller than options, the design-system's option
// padding varies by theme), so these only seed react-window until each row
// reports its measured height.
const OPTION_ROW_HEIGHT = 40
const GROUP_HEADER_ROW_HEIGHT = 48
const MAX_VISIBLE_ROWS = 8
const OVERSCAN_COUNT = 10

export interface VirtualizedListboxProps extends HTMLAttributes<HTMLElement> {
  multiControls?: boolean
  checkbox?: boolean
  selectedCount?: number
  optionCount?: number
  onClearAll: () => void
  onSelectAll: () => void
}

// MUI hands the Listbox an array of children. Without grouping each child is an
// option tuple `[props, option, state]` (what renderOption returns). With the
// raw renderGroup, each child is instead a group descriptor object
// `{ key, group, children }` whose `children` are the option tuples for that
// group. We flatten both shapes into a single list of rows.
type OptionTuple = [HTMLAttributes<HTMLLIElement> & { key?: string }, unknown, { selected?: boolean }]
type GroupRow = { key?: string; group: ReactNode; children?: OptionTuple[] }
type Row = OptionTuple | GroupRow

interface RowData {
  rows: Row[]
  setRowHeight: (index: number, height: number) => void
}

const isGroupRow = (row: Row): row is GroupRow => !Array.isArray(row)

const getRowEstimate = (row: Row) => (isGroupRow(row) ? GROUP_HEADER_ROW_HEIGHT : OPTION_ROW_HEIGHT)

const getRowKey = (row: Row, index: number) =>
  isGroupRow(row) ? (row.key ?? `group-${index}`) : (row[0].key ?? `option-${index}`)

const StyledGroupHeader = styled(ListSubheader, {
  name: 'AutocompleteField',
  slot: 'VirtualGroupHeader',
  overridesResolver: (props, styles) => styles.virtualGroupHeader,
})(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: theme.typography.pxToRem(GROUP_HEADER_ROW_HEIGHT),
  backgroundColor: theme.palette.background.paper,
}))

// Restore the design-system option's natural padding (the custom virtualized
// Popper doesn't inherit MUI's default option styling) and keep each option on
// a single line: flex aligns the checkbox + label, and the label truncates with
// an ellipsis instead of wrapping. Height is left to the content + padding and
// measured at runtime, so spacing matches the non-virtualized list.
const StyledVirtualOption = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  padding: theme.spacing(0.75, 2),
  cursor: 'pointer',
  // The custom virtualized Popper doesn't inherit MUI's option state styles, so
  // restore hover / keyboard-focus / selected backgrounds to match the
  // non-virtualized list.
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [`&.${autocompleteClasses.focused}`]: {
    backgroundColor: theme.palette.action.hover,
  },
  '&[aria-selected="true"]': {
    backgroundColor: theme.palette.action.selected,
  },
  [`&[aria-selected="true"].${autocompleteClasses.focused}`]: {
    backgroundColor: theme.palette.action.selected,
  },
}))

// Measures its own natural height and reports it up whenever it changes, so
// react-window can position variable-height rows exactly. The element receives
// react-window's absolute positioning `style` but with `height` stripped, so the
// seed estimate can't clamp the content and make us measure a wrong (clipped)
// value — the content + padding define the height. A ResizeObserver re-measures
// on reflow (resize, late font load). The measured element is the option/header
// itself (no wrapper) so the flex layout + padding are preserved.
const useMeasuredHeight = (
  index: number,
  setRowHeight: (index: number, height: number) => void,
  deps: ReactNode,
) => {
  const ref = useRef<HTMLLIElement>(null)

  useLayoutEffect(() => {
    const node = ref.current

    if (node == null) {
      return
    }

    const measure = () => {
      const measured = node.getBoundingClientRect().height || node.offsetHeight

      if (measured > 0) {
        setRowHeight(index, measured)
      }
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(measure)
    observer.observe(node)

    return () => observer.disconnect()
    // `deps` (the row content) re-measures when an index is reused for new content.
  }, [index, setRowHeight, deps])

  return ref
}

// react-window re-renders the visible window on every scroll tick; memoizing the
// row so it only re-renders when its index/style/data actually change keeps fast
// scrolling smooth.
const Row = memo(function Row(rowProps: ListChildComponentProps<RowData>) {
  const { data, index, style } = rowProps
  const row = data.rows[index]

  // `height` is dropped so the row sizes to its natural content + padding; `top`
  // is offset by the listbox padding.
  const { height: _height, ...positionStyle } = style
  const positionedStyle = { ...positionStyle, top: (style.top as number) + LISTBOX_PADDING }

  const isHeader = isGroupRow(row)
  const content = isHeader ? row.group : row[0].children
  const measureRef = useMeasuredHeight(index, data.setRowHeight, content)

  if (isHeader) {
    return (
      <StyledGroupHeader ref={measureRef} style={positionedStyle} className={autocompleteClasses.groupLabel}>
        {row.group}
      </StyledGroupHeader>
    )
  }

  // optionProps carries MUI's option a11y attributes (role, aria-selected) and
  // the shared option body as `children`.
  const [optionProps] = row

  return (
    <StyledVirtualOption {...optionProps} ref={measureRef} style={positionedStyle}>
      {optionProps.children}
    </StyledVirtualOption>
  )
})

// Context lets MUI's listbox props (role, onMouseDown, id, etc.) reach
// react-window's outer scroll container so a11y + interaction survive windowing.
const OuterElementContext = createContext<HTMLAttributes<HTMLElement>>({})

const OuterElementType = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function OuterElementType(
  props,
  ref,
) {
  const outerProps = useContext(OuterElementContext)
  // react-window's `style` carries the scroller layout (height, overflow:auto,
  // position) and must win over MUI's listbox props, or the element stops
  // scrolling. But MUI's className (the listbox slot classes) and role must be
  // preserved — react-window passes its own (undefined) className which would
  // otherwise clobber them — so merge className and let react-window's style win.
  const className = [outerProps.className, props.className].filter(Boolean).join(' ') || undefined

  return (
    <div ref={ref} {...outerProps} {...props} className={className} style={{ ...outerProps.style, ...props.style }} />
  )
})

// Reset the VariableSizeList measurement cache whenever the row count changes
// (filtering / a new option set) so cached offsets for the old length aren't
// reused. Fires on filter changes, never mid-scroll.
const useResetCache = (itemCount: number) => {
  const ref = useRef<VariableSizeList>(null)

  useEffect(() => {
    ref.current?.resetAfterIndex(0, true)
  }, [itemCount])

  return ref
}

// Per-index measured-height store, reconciled before react-window reads itemSize
// so a row whose identity changed at a reused index re-measures (no stale value).
interface MeasureStore {
  heights: (number | undefined)[]
  keys: (string | undefined)[]
}

const reconcileStore = (store: MeasureStore, rows: Row[]) => {
  for (let index = 0; index < rows.length; index += 1) {
    const key = getRowKey(rows[index], index)

    if (store.keys[index] !== key) {
      store.keys[index] = key
      store.heights[index] = undefined
    }
  }

  if (store.keys.length > rows.length) {
    store.keys.length = rows.length
    store.heights.length = rows.length
  }
}

const _VirtualizedListbox = forwardRef<HTMLDivElement, VirtualizedListboxProps>(function _VirtualizedListbox(
  props,
  ref,
) {
  // `checkbox` is destructured only to keep it off the DOM (row heights are now
  // measured, so it's no longer needed for sizing).
  const { children, multiControls, checkbox: _checkbox, selectedCount, optionCount, onClearAll, onSelectAll, ...other } =
    props

  // MUI attaches onMouseDown to the listbox root and preventDefaults so the input
  // keeps focus. The multiControls live OUTSIDE react-window's viewport, so we
  // replay the handler on their wrapper to keep the dropdown open.
  const { onMouseDown } = other

  const rows = useMemo<Row[]>(() => {
    const items = (Array.isArray(children) ? children : [children]) as Row[]

    return items.flatMap((item) => (isGroupRow(item) ? [item, ...(item.children ?? [])] : [item]))
  }, [children])

  const itemCount = rows.length
  const gridRef = useResetCache(itemCount)

  const storeRef = useRef<MeasureStore>({ heights: [], keys: [] })
  reconcileStore(storeRef.current, rows)

  const setRowHeight = useCallback(
    (index: number, height: number) => {
      const store = storeRef.current

      if (store.heights[index] === height) {
        return
      }

      store.heights[index] = height
      gridRef.current?.resetAfterIndex(index)
    },
    [gridRef],
  )

  const getChildSize = (index: number) => storeRef.current.heights[index] ?? getRowEstimate(rows[index])

  // Cap the viewport at MAX_VISIBLE_ROWS using the measured-or-estimated sizes of
  // the first rows so a list led by a tall group header isn't under-sized.
  const viewportRows = Math.min(itemCount, MAX_VISIBLE_ROWS)
  let visibleHeight = 0
  for (let index = 0; index < viewportRows; index += 1) {
    visibleHeight += getChildSize(index)
  }

  const totalHeight = rows.reduce((acc, _row, index) => acc + getChildSize(index), 0)
  const height = itemCount > MAX_VISIBLE_ROWS ? visibleHeight : totalHeight

  const itemData = useMemo<RowData>(() => ({ rows, setRowHeight }), [rows, setRowHeight])

  return (
    <div ref={ref}>
      {multiControls && (
        // role="presentation": this wrapper only replays MUI's onMouseDown to keep
        // input focus; it is not itself an interactive control. paddingTop restores
        // the listbox top padding the windowed popper resets to 0.
        <div role="presentation" onMouseDown={onMouseDown} style={{ paddingTop: LISTBOX_PADDING }}>
          <AutocompleteFieldMultiControls
            ownerId={other.id}
            selectedCount={selectedCount || 0}
            optionCount={optionCount || 0}
            onSelectAll={onSelectAll}
            onClearAll={onClearAll}
          />
        </div>
      )}
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={height + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={getChildSize}
          overscanCount={OVERSCAN_COUNT}
          itemCount={itemCount}
          itemKey={(index, data) => getRowKey(data.rows[index], index)}
        >
          {Row}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})

const VirtualizedListbox = _VirtualizedListbox
export default VirtualizedListbox
